import { trackCtaClick } from '../../utils/ctaTracking';
import { buildAppUrl } from '../../utils/appUrl';
import type { LandingContent } from '../content';
import { Reveal } from '../components/Reveal';
import { SectionContainer } from '../components/SectionContainer';
import type { TrackingContext } from '../types';

type CommunityBooksSectionProps = {
  content: LandingContent['communityBooks'];
  tracking: TrackingContext;
};

export function CommunityBooksSection({ content, tracking }: CommunityBooksSectionProps) {
  const hasSingleBook = content.books.length === 1;

  return (
    <section className="bg-[#F7F7FA] py-20 text-[#1C1C1E] sm:py-24 lg:py-[108px]">
      <SectionContainer className="max-w-[1280px]">
        <Reveal>
          <h2 className="text-center font-pangaia text-[40px] font-bold leading-[1.02] tracking-[-0.03em] sm:text-[56px]">
            {content.title}
          </h2>
        </Reveal>

        <div className={`mt-10 flex gap-4 overflow-x-auto px-1 pb-4 no-scrollbar snap-x snap-mandatory sm:mt-12 lg:gap-6 ${hasSingleBook ? 'justify-center' : ''}`}>
          {content.books.map((book, index) => (
            <Reveal
              key={book.id}
              delay={index * 80}
              className="snap-start min-w-[264px] max-w-[264px] sm:min-w-[306px] sm:max-w-[306px] lg:min-w-[336px] lg:max-w-[336px]"
            >
              <a
                href={buildAppUrl(book.href)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(event) => {
                  trackCtaClick({
                    placement: 'community_books',
                    language: tracking.language,
                    destination: event.currentTarget.href,
                    hasAnalyticsConsent: tracking.hasAnalyticsConsent,
                  });
                }}
                className="group block h-full overflow-hidden rounded-[18px] border border-[#E6E9F3] shadow-[0_16px_44px_rgba(28,28,30,0.08)] transition-transform duration-200 hover:-translate-y-1"
              >
                <div className="relative h-[388px] bg-[#0C1018] sm:h-[430px] lg:h-[446px]">
                  {book.imageUrl ? (
                    <img
                      src={book.imageUrl}
                      alt={book.title}
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-[linear-gradient(135deg,#B7CCFF_0%,#8B77FF_54%,#78D3DC_100%)]" />
                  )}
                  <div
                    className="absolute inset-0"
                    aria-hidden="true"
                    style={{
                      background:
                        'linear-gradient(180deg, rgba(0,0,0,0.04) 0%, rgba(0,0,0,0.08) 22%, rgba(0,0,0,0.18) 42%, rgba(0,0,0,0.52) 70%, rgba(0,0,0,0.82) 100%)',
                    }}
                  />

                  <div
                    className="absolute inset-x-0 bottom-0 z-10 p-3 pt-16 text-white sm:p-5 sm:pt-24"
                    style={{
                      background:
                        'linear-gradient(0deg, rgba(0, 0, 0, 0.76) 0%, rgba(0, 0, 0, 0.18) 42%, transparent 100%)',
                    }}
                  >
                    <h3 className="max-w-[220px] font-pangaia text-[18px] font-semibold leading-[1.05] tracking-[-0.03em] text-white sm:max-w-[252px] sm:text-[20px]">
                      {book.title}
                    </h3>

                    <div className="mt-2 flex min-h-[24px] flex-wrap items-center gap-x-2 gap-y-1 text-white/84">
                      <span className="inline-flex items-center gap-2 font-pangram text-[12px] font-medium leading-[16px] text-white/88">
                        <span className="h-2 w-2 rounded-full bg-[#E1B64B]" aria-hidden="true" />
                        {book.genre}
                      </span>
                      {book.authorName ? (
                        <>
                          <span className="h-[2px] w-[2px] rounded-full bg-white/72" aria-hidden="true" />
                          <span className="inline-flex items-center gap-2 font-pangram text-[12px] font-medium leading-[16px] text-white/88">
                            {book.authorAvatarUrl ? (
                              <img
                                src={book.authorAvatarUrl}
                                alt=""
                                className="h-6 w-6 rounded-full object-cover"
                                loading="lazy"
                              />
                            ) : (
                              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/20 text-[10px] font-semibold text-white">
                                {book.authorName.slice(0, 1)}
                              </span>
                            )}
                            {book.authorName}
                          </span>
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
              </a>
            </Reveal>
          ))}
        </div>
      </SectionContainer>
    </section>
  );
}
